// A4.1 Issue side panel — opened by clicking a row on A4 (not its checkbox).
// Snapshot card above the Issue Description card (03/Oct/26). × or Esc collapses it; ⤢ opens A5.
// v0.6.0 Part 6: the snapshot slider (newest first, view only here); double-click a picture → A5.1 viewer.
// View only (round 13 #9): a "Latest resolution" card shows the newest log entry + "N entries".
import { href } from "../../../core/router";
import { formatDate } from "../../../core/format";
import { InfoIcon, StatusPill } from "../../../core/ui/kit";
import type { Discipline, IssueRow } from "../../../core/api/types";
import { useState } from "react";
import { SnapshotSlider, SnapshotViewer, useIssueSnapshots } from "./Snapshots";
import { Icon } from "../../../core/ui/icons";

export function IssueSidePanel({ row, code, prefix, disciplines, onClose }: { row: IssueRow; code: string; prefix: string; disciplines: Discipline[]; onClose: () => void }) {
  const d = disciplines.find((x) => x.code === row.discipline);
  const snapQ = useIssueSnapshots(row.id);
  const snaps = (snapQ.data?.snapshots ?? []).filter((s) => !s.log_id);     // v0.7.1: the card shows the issue's own snapshots
  const [viewing, setViewing] = useState<number | null>(null);
  return (
    <aside className="side-panel" aria-label={`Issue ${row.code}`} data-testid="side-panel">
      <div className="row between">
        <div className="row gap6">
          <b className="code big">{row.code}</b><StatusPill status={row.status} />
          <span className="pill p-tag" style={{ color: d?.colour }}>{row.discipline}</span>
          <InfoIcon tip={["View only.", "Latest resolution = the newest log entry; add entries on the issue page.", "× or Esc collapses the panel.", "⤢ or double-click opens the full issue detail."]} />
        </div>
        <div className="row gap6">
          <a className="btn icon" href={href({ name: "issue", code, prefix, issue: row.code })} aria-label="Open issue detail" title="Open issue detail"><Icon name="open_in_full" /></a>
          <button className="btn icon" onClick={onClose} aria-label="Close panel"><Icon name="close" /></button>
        </div>
      </div>
      <SnapshotSlider className="panel" code={row.code} snaps={snaps} canEdit={false} expand={false} loading={!snapQ.data} onAdd={() => {}} onOpen={setViewing} />
      {viewing !== null && snaps.length > 0 && <SnapshotViewer code={row.code} snaps={snaps} start={viewing} onClose={() => setViewing(null)} />}
      {/* v0.8.1 (round 29 #4, board A4.1): Location under the picture card */}
      <div className="field-card" data-testid="panel-location">
        <p className="k">Location</p>
        <p className="v">{row.location || "–"}</p>
      </div>
      <div className="field-card">
        <p className="k">Issue Description</p>
        <p className="v">{row.description}</p>
      </div>
      <div className="field-card latest-log" data-testid="latest-log">
        <div className="row between"><p className="k">Latest resolution</p><span className="muted tiny" data-testid="log-count">{row.log_count} {row.log_count === 1 ? "entry" : "entries"}</span></div>
        {row.latest_log ? <>
          <p className="v strong">{formatDate(row.latest_log.entry_date)}</p>
          <p className="v clamp4">{row.latest_log.note}</p>
          <p className="muted tiny">Action by: {row.latest_log.action_by || "–"}</p>
        </> : <p className="muted small">No resolution logged yet</p>}
      </div>
    </aside>
  );
}
